import React, { useState } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import ChildFriendlyIcon from "@mui/icons-material/ChildFriendly";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";

const EXTRA_ICONS = {
  babySeat: <ChildCareIcon sx={{ fontSize: 28 }} />,
  babySeatIsofix: <ChildFriendlyIcon sx={{ fontSize: 28 }} />,
  boosterSeat: <EventSeatIcon sx={{ fontSize: 28 }} />,
};

const ExtrasIncrementer = ({
  extras,
  setExtras,
  totalDays,
  insuranceValue,
  vehicleGroup,
  pickUpDesk,
  dropOffDesk,
}) => {
  const { t } = useTranslation();
  const [fullInsurance, setFullInsurance] = useState(0);

  const checkExcessValue = (group) => {
    switch (group) {
      case "SCOOTER":
        return `${t("excessValue")} 550€`;
      case "MDMV":
      case "NBMV":
      case "NCMV":
        return `${t("excessValue")} 1000€`;
      case "EDMV":
      case "EDMD":
      case "HXMD":
      case "MDAV":
      case "EDAV":
        return `${t("excessValue")} 1500€`;
      case "CFMV":
      case "CDMV":
      case "CGMD":
      case "CDMD":
      case "HDMD":
      case "CMMR":
      case "CWMD":
      case "NTMV":
      case "CTMV":
      case "CTAV":
      case "CDAV":
      case "CDAD":
      case "SDMD":
      case "RFMD":
      case "CGAV":
      case "CGAD":
      case "CMMD":
      case "TXT":
        return `${t("excessValue")} 2000€`;
      case "LVMD":
      case "RVAD":
      case "DDAD":
      case "SVMD":
      case "SFAD":
      case "JVMR":
        return `${t("excessValue")} 3000€`;
      case "PDAD":
        return `${t("excessValue")} 3800€`;
      default:
        return `${t("excessValue")} 2000€`;
    }
  };

  const handleIncrement = (realIndex) => {
    const updatedExtras = [...extras];
    const extra = updatedExtras[realIndex];

    if (extra.id === 5) extra.value = insuranceValue;

    if (extra.quantity >= extra.max) return;

    extra.quantity += 1;

    if (extra.totalByDay) {
      const raw = extra.quantity * extra.value * totalDays;
      extra.totalValue = raw > extra.maxValue ? extra.maxValue : raw;
    } else {
      const raw = extra.quantity * extra.value;
      extra.totalValue = raw > extra.maxValue ? extra.maxValue : raw;
    }

    setExtras(updatedExtras);
  };

  const handleDecrement = (realIndex) => {
    const updatedExtras = [...extras];
    const extra = updatedExtras[realIndex];
    if (extra.quantity <= extra.min) return;
    extra.quantity -= 1;
    extra.totalValue = extra.quantity * extra.value;
    setExtras(updatedExtras);
  };

  const handleChangeInsurance = (value) => {
    const updatedExtras = [...extras];
    setFullInsurance(value);
    updatedExtras[4].value = insuranceValue;
    updatedExtras[4].quantity = value;
    const raw = updatedExtras[4].quantity * updatedExtras[4].value * totalDays;
    updatedExtras[4].totalValue =
      raw > updatedExtras[4].maxValue ? updatedExtras[4].maxValue : raw;
    setExtras(updatedExtras);
  };

  const extraPerDayLabel = (id) => {
    return [0, 1, 2, 5].includes(id) ? t("perDay") : t("perRent");
  };

  const regularExtras = extras?.filter(
    (e) => e.id !== 3 && e.id !== 4 && e.id !== 5 && e.id !== 7 && e.id !== 9 && e.id !== 10,
  );

  const insuranceExtra = extras?.find((e) => e.id === 5);

  return (
    <Stack spacing={2.5}>
      {/* ── Header ── */}
      <Box>
        <Typography variant="h5" fontWeight={700} color="#1C2541">
          {t("extrasTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("extrasSubtitle")}
        </Typography>
      </Box>

      {/* ── Regular extras (baby seats etc.) ── */}
      {regularExtras?.map((extra) => {
        const realIndex = extras.findIndex((e) => e.id === extra.id);
        const active = extra.quantity > 0;

        return (
          <Card
            key={extra.id}
            elevation={0}
            sx={{
              border: `1.5px solid ${active ? "#EE8013" : "#e8eaf0"}`,
              borderRadius: 2.5,
              transition: "border-color 0.2s",
              overflow: "visible",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 2 }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: active ? "#fff3e0" : "#f4f6fb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: active ? "#EE8013" : "#1C2541",
                  flexShrink: 0,
                  transition: "background-color 0.2s, color 0.2s",
                }}
              >
                {EXTRA_ICONS[extra.extraName] || <EventSeatIcon sx={{ fontSize: 28 }} />}
              </Box>

              {/* Name + price */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700} color="#1C2541" variant="body1">
                  {t(extra.extraName)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {extra.value}€ / {extraPerDayLabel(extra.id)}
                </Typography>
              </Box>

              {/* Total chip */}
              {active && (
                <Chip
                  label={`${extra.totalValue.toFixed(2)}€`}
                  size="small"
                  sx={{
                    backgroundColor: "#EE8013",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 12,
                    height: 24,
                  }}
                />
              )}

              {/* +/- controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  border: "1.5px solid #e8eaf0",
                  borderRadius: 2,
                  px: 0.5,
                  py: 0.25,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleDecrement(realIndex)}
                  disabled={extra.quantity <= extra.min}
                  sx={{
                    width: 28,
                    height: 28,
                    color: extra.quantity > extra.min ? "#1C2541" : "text.disabled",
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography
                  sx={{
                    minWidth: 24,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 15,
                    color: active ? "#EE8013" : "#1C2541",
                  }}
                >
                  {extra.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleIncrement(realIndex)}
                  disabled={extra.quantity >= extra.max}
                  sx={{
                    width: 28,
                    height: 28,
                    color: extra.quantity < extra.max ? "#EE8013" : "text.disabled",
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Card>
        );
      })}

      {/* ── Insurance section ── */}
      {insuranceExtra && (
        <Card
          elevation={0}
          sx={{
            border: "1.5px solid #1C2541",
            borderRadius: 2.5,
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1C2541",
              px: 2,
              py: 1.25,
              borderRadius: "10px 10px 0 0",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="white"
              sx={{ letterSpacing: 0.3 }}
            >
              {t("chooseInsurance")}
            </Typography>
          </Box>

          <RadioGroup
            value={fullInsurance}
            onChange={(e) => handleChangeInsurance(e.target.value)}
            sx={{ px: 1.5, pt: 1, pb: 1.5 }}
          >
            {/* Full Insurance option */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 2,
                px: 1,
                py: 0.75,
                backgroundColor: Number(fullInsurance) === 1 ? "#fff8f0" : "transparent",
                border: `1px solid ${Number(fullInsurance) === 1 ? "#EE8013" : "transparent"}`,
                transition: "background-color 0.15s, border-color 0.15s",
                mb: 0.5,
              }}
            >
              <FormControlLabel
                value={1}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#EE8013" } }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedUserIcon sx={{ color: "#EE8013", fontSize: 22 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={700} color="#1C2541">
                        {t("fullInsurance")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {insuranceValue.toFixed(2)}€ / {t("perDay")}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ flex: 1, mr: 0 }}
              />
              <Tooltip title={t("fullInsuranceInfo")} placement="top" arrow>
                <InfoOutlinedIcon
                  sx={{ color: "text.secondary", fontSize: 18, cursor: "help", ml: 1 }}
                />
              </Tooltip>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Standard excess option */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 2,
                px: 1,
                py: 0.75,
                backgroundColor: Number(fullInsurance) === 0 ? "#f4f6fb" : "transparent",
                border: `1px solid ${Number(fullInsurance) === 0 ? "#1C2541" : "transparent"}`,
                transition: "background-color 0.15s, border-color 0.15s",
                mt: 0.5,
              }}
            >
              <FormControlLabel
                value={0}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#1C2541" } }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GppMaybeOutlinedIcon sx={{ color: "#1C2541", fontSize: 22 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={700} color="#1C2541">
                        {checkExcessValue(vehicleGroup)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("mandatoryExcess")}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ flex: 1, mr: 0 }}
              />
              <Tooltip title={t("excessInfo")} placement="top" arrow>
                <InfoOutlinedIcon
                  sx={{ color: "text.secondary", fontSize: 18, cursor: "help", ml: 1 }}
                />
              </Tooltip>
            </Box>
          </RadioGroup>
        </Card>
      )}
    </Stack>
  );
};

ExtrasIncrementer.propTypes = {
  extras: PropTypes.any.isRequired,
  setExtras: PropTypes.func.isRequired,
  totalDays: PropTypes.number.isRequired,
  insuranceValue: PropTypes.number.isRequired,
  vehicleGroup: PropTypes.string.isRequired,
  pickUpDesk: PropTypes.number.isRequired,
  dropOffDesk: PropTypes.number.isRequired,
};

export default ExtrasIncrementer;
